import { useState } from "react";
import { useNotify, useRecordContext, useUpdate } from "ra-core";
import {
  Building2,
  Check,
  MoreHorizontal,
  Pencil,
  StickyNote,
  Merge,
  Download,
  Archive,
} from "lucide-react";
import { toast } from "sonner";

import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { Avatar } from "./Avatar";
import { AcquisitionBadge } from "./ContactBadges";
import { ContactEditSheet } from "./ContactEditSheet";
import { TemperaturePopover } from "./TemperaturePopover";
import { NextActionPopover } from "./NextActionPopover";
import { MarkDoneDialog } from "./MarkDoneDialog";
import { downloadVCard } from "./vcard";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import type {
  Contact,
  NextActionType,
  Population,
  Temperature,
} from "../types";

/**
 * Header de la fiche contact refondue Sprint 2.
 * Cf design/atomic-crm/header.jsx (FicheHeader).
 *
 * - Avatar + nom + meta (rôle · entreprise · email).
 * - 2 badges : Population (cliquable toggle) + Température (popover dropdown).
 * - Boîte Prochaine action (popover édition).
 * - Bouton "Marquer fait" → MarkDoneDialog.
 * - Menu "..." → Modifier les infos, Ajouter une note, Fusionner, Exporter vCard, Archive.
 */
export function ContactHeader() {
  const record = useRecordContext<Contact>();
  const notify = useNotify();
  const [update] = useUpdate();

  const [editOpen, setEditOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [markDoneOpen, setMarkDoneOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  if (!record) return null;

  const fullName =
    `${record.first_name ?? ""} ${record.last_name ?? ""}`.trim();
  const emailPrimary = record.email_jsonb?.[0]?.email;

  const optimisticUpdate = (patch: Partial<Contact>, label: string) => {
    update(
      "contacts",
      { id: record.id, data: patch, previousData: record },
      {
        mutationMode: "optimistic",
        onError: () => {
          notify(`Impossible de mettre à jour ${label}`, { type: "error" });
        },
      },
    );
  };

  const togglePopulation = () => {
    const next: Population =
      record.population === "plateforme" ? "prospection" : "plateforme";
    optimisticUpdate({ population: next }, "la population");
  };

  const setTemperature = (t: Temperature) => {
    optimisticUpdate({ temperature: t }, "la température");
  };

  const setNextAction = (v: { type: NextActionType; date: string | null }) => {
    optimisticUpdate(
      {
        next_action_type: v.type,
        next_action_date: v.type === "aucune" ? null : v.date,
      },
      "la prochaine action",
    );
  };

  const hasAction =
    record.next_action_type && record.next_action_type !== "aucune";

  const handleMergeStub = () => {
    toast.info("Fusion disponible bientôt", {
      description:
        "Pour fusionner ce contact, utilise le bouton dans le panneau latéral en attendant la refonte complète (Sprint 2 T2.3).",
    });
  };

  const handleVCard = () => {
    try {
      downloadVCard(record);
      toast.success("vCard téléchargée", {
        description: `${record.first_name} ${record.last_name ?? ""}.vcf`,
      });
    } catch (e) {
      toast.error("Échec export vCard", {
        description: e instanceof Error ? e.message : "Erreur inconnue",
      });
    }
  };

  const handleArchive = () => {
    update(
      "contacts",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () => {
          toast.success("Contact archivé", {
            description: `${fullName} n'apparaît plus dans les listes par défaut.`,
          });
          setArchiveConfirmOpen(false);
        },
        onError: () => {
          toast.error("Impossible d'archiver le contact");
        },
      },
    );
  };

  const handleRestore = () => {
    update(
      "contacts",
      {
        id: record.id,
        data: { archived_at: null },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () => {
          toast.success("Contact restauré");
        },
        onError: () => {
          toast.error("Impossible de restaurer le contact");
        },
      },
    );
  };

  const isArchived = !!record.archived_at;

  return (
    <>
      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />
      <NoteCreateSheet
        open={noteOpen}
        onOpenChange={setNoteOpen}
        contact_id={record.id}
      />
      <MarkDoneDialog
        contact={record}
        open={markDoneOpen}
        onOpenChange={setMarkDoneOpen}
      />

      <Dialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archiver {fullName} ?</DialogTitle>
            <DialogDescription>
              Le contact n'apparaîtra plus dans les listes par défaut. Les
              paiements, notes et conversations existants restent accessibles.
              Tu peux le restaurer à tout moment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setArchiveConfirmOpen(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleArchive}
              className="gap-1.5"
            >
              <Archive className="size-3.5" />
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isArchived && (
        <div className="rounded-md border border-fabrik-orange/40 bg-fabrik-orange-soft px-3 py-2 mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[13px] text-fabrik-orange min-w-0">
            <Archive className="size-3.5 shrink-0" />
            <span className="truncate">
              Contact archivé le{" "}
              {new Date(record.archived_at!).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleRestore}
            className="gap-1.5 h-7 shrink-0"
          >
            <Archive className="size-3" />
            Restaurer
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card px-4 py-4 md:px-5 md:py-5">
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {/* Avatar */}
          <Avatar />

          {/* Identité + meta */}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl font-semibold leading-tight truncate">
              {fullName}
            </h2>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-muted-foreground mt-0.5">
              {record.title && <span>{record.title}</span>}
              {record.title && record.company_id && (
                <span className="text-muted-foreground/60">·</span>
              )}
              {record.company_id != null && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="size-3 shrink-0" />
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                </span>
              )}
              {emailPrimary && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <a
                    href={`mailto:${emailPrimary}`}
                    className="hover:text-foreground truncate"
                  >
                    {emailPrimary}
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              onClick={togglePopulation}
              className={cn(
                "cursor-pointer gap-1.5 px-2.5 py-0.5 text-[11.5px] font-medium border-transparent",
                record.population === "plateforme"
                  ? "bg-fabrik-green-soft text-fabrik-accent-text"
                  : "bg-fabrik-orange-soft text-fabrik-orange",
              )}
              title="Cliquer pour changer"
            >
              <span
                className={cn(
                  "size-2 rounded-full",
                  record.population === "plateforme"
                    ? "bg-fabrik-green"
                    : "bg-fabrik-orange",
                )}
              />
              {record.population === "plateforme"
                ? "Plateforme"
                : "Prospection"}
            </Badge>
            <AcquisitionBadge value={record.acquisition_source} />
            <TemperaturePopover
              value={record.temperature}
              onChange={setTemperature}
            />
          </div>

          {/* Action zone */}
          <div className="flex items-center gap-2 flex-wrap">
            <NextActionPopover
              value={{
                type: record.next_action_type ?? "aucune",
                date: record.next_action_date ?? null,
              }}
              onSave={setNextAction}
            />
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              onClick={() => setMarkDoneOpen(true)}
              disabled={!hasAction}
              title={
                hasAction
                  ? "Marquer la prochaine action comme faite"
                  : "Pas d'action à marquer"
              }
            >
              <Check className="size-3.5" />
              Marquer fait
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-8"
                  aria-label="Plus d'actions"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="size-3.5" />
                  Modifier les infos
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setNoteOpen(true)}>
                  <StickyNote className="size-3.5" />
                  Ajouter une note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMergeStub}>
                  <Merge className="size-3.5" />
                  Fusionner avec…
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleVCard}>
                  <Download className="size-3.5" />
                  Exporter vCard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {isArchived ? (
                  <DropdownMenuItem onClick={handleRestore}>
                    <Archive className="size-3.5" />
                    Restaurer le contact
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setArchiveConfirmOpen(true)}
                    className="text-destructive"
                  >
                    <Archive className="size-3.5" />
                    Archiver
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </>
  );
}
